import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../../api/projects';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectBudgetLine } from '../../../types/projects';
import { BUDGET_CATEGORIES, BudgetCategoryKey } from './budgetCategories';
import { CommentThread } from '../../../components/CommentThread';
import { isProjectDesignEditable } from './projectEditLock';

/**
 * Project budget, following the OCHA pooled-fund model (OneGMS budget
 * tab): direct-cost categories A-G, each line itemised as Quantity x
 * Unit Cost; then Programme Support Costs (PSC) entered as a single
 * rate of at most 7% applied to total direct costs, never itemised;
 * then the grand total. Every total shown here is recomputed from the
 * lines, and the server independently enforces that direct costs plus
 * PSC never exceed the approved budget.
 */
const lineSchema = z.object({
  description: z.string().min(2, 'Description is required.'),
  remarks: z.string().optional(),
  unitDescription: z.string().optional(),
  unitQuantity: z.coerce.number().positive('Must be greater than zero.'),
  unitCost: z.coerce.number().positive('Must be greater than zero.'),
  duration: z.coerce.number().positive('Must be greater than zero.'),
  durationUnit: z.enum(['months', 'weeks', 'days', 'one_off']),
  pctCharged: z.coerce.number().gt(0, 'Must be greater than zero.').max(100, 'Cannot exceed 100%.'),
});
type LineFormValues = z.infer<typeof lineSchema>;

const MAX_PSC_RATE = 7;
const DURATION_UNITS: Record<string, string> = { months: 'months', weeks: 'weeks', days: 'days', one_off: 'one-off' };
const LINE_DEFAULTS = { unitQuantity: 1, duration: 1, durationUnit: 'months' as const, pctCharged: 100 };

function money(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const lineAmount = (l: ProjectBudgetLine) => Number(l.line_total ?? l.planned_amount);
/** Full cost before sharing: Qty x Unit Cost x Duration. The charged total (lineAmount) is this x % charged to donor. */
const lineSubtotal = (l: ProjectBudgetLine) => Math.round(Number(l.unit_quantity) * Number(l.unit_cost ?? 0) * Number(l.duration ?? 1) * 100) / 100;
const durationText = (l: ProjectBudgetLine) => (l.duration_unit === 'one_off' ? 'one-off' : `${Number(l.duration ?? 1)} ${DURATION_UNITS[l.duration_unit ?? 'months']}`);
const pctOf = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '-');

export function ProjectBudgetTab({ project }: { project: Project }) {
  const canManage = usePermission('project_budget.manage') && isProjectDesignEditable(project.status);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const savedRate = Number(project.psc_rate ?? 0);
  const [pscDraft, setPscDraft] = useState(String(savedRate));

  const budgetQuery = useQuery({ queryKey: ['project-budget', project.id], queryFn: () => projectsApi.listBudgetLines(project.id) });
  const lines = budgetQuery.data?.data ?? [];

  const pscMutation = useMutation({
    mutationFn: (rate: number) => projectsApi.setPscRate(project.id, rate),
    onSuccess: () => { showSuccess('PSC rate saved.'); queryClient.invalidateQueries({ queryKey: ['project', project.id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save PSC rate.'),
  });

  const directTotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);
  const pscAmount = Math.round(directTotal * savedRate) / 100;
  const grandTotal = directTotal + pscAmount;
  const approved = Number(project.budget_total);
  const unallocated = approved - grandTotal;
  const draftRate = Number(pscDraft);
  const draftInvalid = pscDraft.trim() === '' || Number.isNaN(draftRate) || draftRate < 0 || draftRate > MAX_PSC_RATE;
  const pct = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '-');

  return (
    <>
      <section className="card budget-summary-card">
        <h2>Budget</h2>
        <div className="budget-summary-row">
          <div><span className="muted">Approved budget</span><strong>{project.currency_code} {money(approved)}</strong></div>
          <div><span className="muted">Planned (direct + PSC)</span><strong>{project.currency_code} {money(grandTotal)}</strong></div>
          <div><span className="muted">Unallocated</span><strong className={unallocated < 0 ? 'budget-negative' : ''}>{project.currency_code} {money(unallocated)}</strong></div>
        </div>
        <div className="budget-export-actions">
          <a className="btn-secondary" href={`/api/projects/${project.id}/budget/export?format=xlsx`}>Export Excel</a>
          <a className="btn-secondary" href={`/api/projects/${project.id}/budget/export?format=pdf`}>Export PDF</a>
        </div>
        <CommentThread projectId={project.id} entityType="budget" />
      </section>

      {BUDGET_CATEGORIES.map((cat) => (
        <BudgetCategorySection
          key={cat.key}
          projectId={project.id}
          currencyCode={project.currency_code}
          category={cat}
          lines={lines.filter((l) => l.category === cat.key)}
          canManage={canManage}
          grandTotal={grandTotal}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['project-budget', project.id] })}
        />
      ))}

      <section className="card budget-category-card">
        <div className="page-header">
          <h3><span className="code-badge">H</span> Indirect Programme Support Costs (PSC)</h3>
        </div>
        <p className="muted" style={{ fontSize: 12.5 }}>
          Headquarters, regional or country-level costs that can't be traced to specific activities. Entered as a rate of at most {MAX_PSC_RATE}% of total direct costs (categories A-G) and calculated automatically; not itemised.
        </p>
        <div className="budget-psc-row">
          {canManage ? (
            <>
              <label className="budget-psc-rate">PSC Rate (%)
                <input type="number" min={0} max={MAX_PSC_RATE} step="0.01" value={pscDraft} onChange={(e) => setPscDraft(e.target.value)} />
              </label>
              <button onClick={() => pscMutation.mutate(draftRate)} disabled={draftInvalid || pscMutation.isPending || draftRate === savedRate}>
                {pscMutation.isPending ? 'Saving…' : 'Save Rate'}
              </button>
              {draftInvalid && <span className="field-error">Enter a rate from 0 to {MAX_PSC_RATE}.</span>}
            </>
          ) : (
            <span>PSC Rate: <strong>{savedRate}%</strong></span>
          )}
          <span className="budget-psc-amount">PSC Amount: <strong>{project.currency_code} {money(pscAmount)}</strong></span>
        </div>
      </section>

      <section className="card">
        <h2>Budget Summary</h2>
        <div className="gantt-scroll">
          <table className="budget-table budget-summary-table">
            <thead>
              <tr><th>Code</th><th>Category</th><th className="num">Amount ({project.currency_code})</th><th className="num">% of Total</th></tr>
            </thead>
            <tbody>
              {BUDGET_CATEGORIES.map((cat) => {
                const amount = lines.filter((l) => l.category === cat.key).reduce((s, l) => s + lineAmount(l), 0);
                return (
                  <tr key={cat.key}>
                    <td>{cat.code}</td><td>{cat.label}</td><td className="num">{money(amount)}</td><td className="num">{pct(amount, grandTotal)}</td>
                  </tr>
                );
              })}
              <tr className="budget-summary-subtotal"><td /><td>Total Direct Costs (A-G)</td><td className="num">{money(directTotal)}</td><td className="num">{pct(directTotal, grandTotal)}</td></tr>
              <tr><td>H</td><td>Indirect Programme Support Costs (PSC) at {savedRate}%</td><td className="num">{money(pscAmount)}</td><td className="num">{pct(pscAmount, grandTotal)}</td></tr>
              <tr className="budget-summary-grand"><td /><td>Grand Total</td><td className="num">{money(grandTotal)}</td><td className="num">{grandTotal > 0 ? '100.0%' : '-'}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Approved budget {project.currency_code} {money(approved)} · {unallocated >= 0 ? `Unallocated ${project.currency_code} ${money(unallocated)}` : `Over by ${project.currency_code} ${money(-unallocated)}`}
        </p>
      </section>
    </>
  );
}

function BudgetCategorySection({
  projectId, currencyCode, category, lines, canManage, grandTotal, onChanged,
}: {
  projectId: number;
  currencyCode: string;
  category: { key: BudgetCategoryKey; code: string; label: string };
  lines: ProjectBudgetLine[];
  canManage: boolean;
  grandTotal: number;
  onChanged: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<LineFormValues>({
    resolver: zodResolver(lineSchema),
    defaultValues: LINE_DEFAULTS,
  });
  const [wq, wc, wd, wp] = watch(['unitQuantity', 'unitCost', 'duration', 'pctCharged']);
  const preview = [wq, wc, wd, wp].every((v) => Number(v) > 0) ? Math.round(Number(wq) * Number(wc) * Number(wd) * Number(wp)) / 100 : null;

  const closeForm = () => { setFormOpen(false); setEditingId(null); reset({ description: '', remarks: '', unitDescription: '', unitCost: undefined as unknown as number, ...LINE_DEFAULTS }); };

  const saveMutation = useMutation({
    mutationFn: (values: LineFormValues): Promise<unknown> => editingId
      ? projectsApi.updateBudgetLine(projectId, editingId, values)
      : projectsApi.addBudgetLine(projectId, { ...values, category: category.key, currencyCode }),
    onSuccess: () => { showSuccess(editingId ? 'Budget line updated.' : 'Budget line added.'); onChanged(); closeForm(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save budget line.'),
  });
  const deleteMutation = useMutation({
    mutationFn: (lineId: number) => projectsApi.deleteBudgetLine(projectId, lineId),
    onSuccess: () => { showSuccess('Budget line deleted.'); onChanged(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete budget line.'),
  });

  const startEdit = (l: ProjectBudgetLine) => {
    setEditingId(l.id);
    setFormOpen(true);
    reset({ description: l.description ?? '', remarks: l.remarks ?? '', unitDescription: l.unit_description ?? '', unitQuantity: Number(l.unit_quantity), unitCost: Number(l.unit_cost ?? 0), duration: Number(l.duration ?? 1), durationUnit: l.duration_unit ?? 'months', pctCharged: Number(l.pct_charged ?? 100) });
  };

  const categorySubtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);
  const categoryFullSubtotal = lines.reduce((sum, l) => sum + lineSubtotal(l), 0);
  const colCount = canManage ? 11 : 10;

  return (
    <section className="card budget-category-card">
      <div className="page-header">
        <h3><span className="code-badge">{category.code}</span> {category.label}</h3>
        {canManage && <button className="btn-accent-3" onClick={() => (formOpen ? closeForm() : setFormOpen(true))}>{formOpen ? 'Cancel' : '+ Add Line'}</button>}
      </div>

      <div className="gantt-scroll">
        <table className="budget-table budget-grid">
          <thead>
            <tr>
              <th>Code</th><th>Budget Line</th><th>Unit</th><th className="num">Qty</th><th className="num">Unit Cost</th><th className="num">Duration</th>
              <th className="num">Subtotal</th><th className="num budget-col-pct">% Charged to Donor</th><th className="num">Total Charged</th><th className="num">% of Grand Total</th>
              {canManage && <th />}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && <tr><td colSpan={colCount} className="muted">No budget lines in this category yet.</td></tr>}
            {lines.map((l) => {
              const pct = Number(l.pct_charged ?? 100);
              return (
                <tr key={l.id} className={editingId === l.id ? 'budget-row-editing' : undefined}>
                  <td><span className="code-badge code-badge-activity">{l.line_code}</span></td>
                  <td>{l.description}{l.remarks && <div className="muted budget-remarks">{l.remarks}</div>}</td>
                  <td>{l.unit_description ?? '-'}</td>
                  <td className="num">{Number(l.unit_quantity).toLocaleString()}</td>
                  <td className="num">{money(Number(l.unit_cost ?? 0))}</td>
                  <td className="num">{durationText(l)}</td>
                  <td className="num">{money(lineSubtotal(l))}</td>
                  <td className="num budget-col-pct"><span className={'budget-pct-chip' + (pct < 100 ? ' budget-pct-chip-shared' : '')}>{pct}%</span></td>
                  <td className="num"><strong>{money(lineAmount(l))}</strong></td>
                  <td className="num">{pctOf(lineAmount(l), grandTotal)}</td>
                  {canManage && (
                    <td className="budget-row-actions">
                      <button className="link-button" onClick={() => startEdit(l)}>Edit</button>
                      <button className="link-button link-button-danger" disabled={deleteMutation.isPending}
                        onClick={() => { if (window.confirm(`Delete budget line ${l.line_code} "${l.description}"? This is recorded in the audit trail.`)) deleteMutation.mutate(l.id); }}>Delete</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="budget-grid-subtotal">
              <td colSpan={6}>{category.code} Subtotal</td>
              <td className="num">{money(categoryFullSubtotal)}</td>
              <td className="budget-col-pct" />
              <td className="num">{currencyCode} {money(categorySubtotal)}</td>
              <td className="num">{pctOf(categorySubtotal, grandTotal)}</td>
              {canManage && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {formOpen && (
        <form className="inline-form budget-line-form" onSubmit={handleSubmit((values) => saveMutation.mutate(values))}>
          {editingId && <p className="muted" style={{ width: '100%', margin: 0 }}>Editing line {lines.find((l) => l.id === editingId)?.line_code}. To move a line to another category, delete it and add it there.</p>}
          <label>Budget Line Description<input {...register('description')} />{errors.description && <span className="field-error">{errors.description.message}</span>}</label>
          <label>Remarks (optional)<textarea rows={2} {...register('remarks')} /></label>
          <label>Unit<input placeholder="e.g. month, school, lump sum" {...register('unitDescription')} /></label>
          <label>Quantity<input type="number" step="0.01" {...register('unitQuantity')} />{errors.unitQuantity && <span className="field-error">{errors.unitQuantity.message}</span>}</label>
          <label>Unit Cost<input type="number" step="0.01" {...register('unitCost')} />{errors.unitCost && <span className="field-error">{errors.unitCost.message}</span>}</label>
          <label>Duration<input type="number" step="0.01" {...register('duration')} />{errors.duration && <span className="field-error">{errors.duration.message}</span>}</label>
          <label>Duration Unit
            <select {...register('durationUnit')}>{Object.entries(DURATION_UNITS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
          <label>% Charged to Donor<input type="number" step="0.01" min="0" max="100" {...register('pctCharged')} />{errors.pctCharged && <span className="field-error">{errors.pctCharged.message}</span>}</label>
          <p className="budget-line-preview">Total charged: <strong>{preview !== null ? `${currencyCode} ${money(preview)}` : '-'}</strong> <span className="muted">(Subtotal = Qty × Unit Cost × Duration, then × % charged to donor)</span></p>
          <button type="submit" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Add'}</button>
        </form>
      )}
    </section>
  );
}
